import { Children, FC, PropsWithChildren } from "react";

import {
  ArweaveLogo,
  CoinsHand,
  DotsHorizontal,
  EtherscanLogo,
} from "@hl/base-components/lib/assets/icons.generated";
import { SECONDARY_COLOR } from "@hl/base-components/lib/theme/button";
import { TEXT_COLOR } from "@hl/base-components/lib/theme/colors";
import { WEIGHT_BOLD } from "@hl/base-components/lib/theme/typography";
import BidModalHighlight from "@hl/shared-features/lib/features/marketplace/modals/BidModal";
import { useModalStack } from "@hl/shared-features/lib/features/modal";
import {
  buildContractTokenUrl,
  getBlockExplorerName,
  networkLookup,
} from "@hl/shared-features/lib/utils/blockExplorer";
import { getCurrencySymbol } from "@hl/shared-features/lib/utils/currency";
import {
  Box,
  Button,
  createStyles,
  Grid,
  Group,
  Menu,
  Stack,
  StackProps,
  Text,
  Title,
  useMantineTheme,
} from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useCollections } from "@reservoir0x/reservoir-kit-ui";
import { Link } from "react-router-dom";

import { MINT_URL } from "~config";
import MarketplaceHeaderSkeleton from "~features/marketplace/skeleton/MarketplaceHeaderSkeleton";
import useMintState from "~hooks/useMintState";

import CollectionCreatedBy from "../MintPage/CollectionCreatedBy";

const useStyles = createStyles({
  title: {
    wordBreak: "break-all",
  },
  link: {
    textDecoration: "none",
    "&:hover": {
      textDecoration: "underline",
    },
  },
});

type MarketplaceHeaderProps = StackProps;

const MarketplaceHeader = ({ ...rest }: MarketplaceHeaderProps) => {
  const {
    collection,
    chainId,
    mintVector,
    totalMintEarned,
    mintVectorsLoading,
    address,
    arweaveLink,
    totalSoldCount,
    isMarketplaceEnabledForCollectionChain,
    isImported,
    reservoirCollectionId,
  } = useMintState();
  const blockchain = networkLookup(chainId);
  const { classes } = useStyles();
  const { pushModal } = useModalStack();

  const { data, isLoading } = useCollections(
    {
      id: reservoirCollectionId,
    },
    undefined,
    chainId
  );

  const marketplaceCollection = data[0];

  if (!collection) {
    return null;
  }

  const currency = getCurrencySymbol(collection?.chainId || "");

  return (
    <Stack spacing={40} {...rest}>
      <Group position="apart">
        <Stack spacing="sm">
          <Title className={classes.title}>{collection.name}</Title>
          <Group spacing={8}>
            <CollectionCreatedBy />
            <Text size="sm">·</Text>
            <Link
              className={classes.link}
              to={MINT_URL.tokens.mint.replace(":collectionId", collection.id)}
            >
              <Text size="sm">View project page</Text>
            </Link>
          </Group>
        </Stack>
        <Group spacing={8}>
          {isMarketplaceEnabledForCollectionChain && (
            <Button
              px="md"
              color={SECONDARY_COLOR}
              size="lg"
              onClick={() =>
                pushModal(
                  <BidModalHighlight
                    src={
                      collection?.collectionImage ||
                      marketplaceCollection?.image ||
                      ""
                    }
                    collectionData={collection}
                  />,
                  {
                    size: "lg",
                    title: (
                      <Text size="md" fw={WEIGHT_BOLD}>
                        Make an offer
                      </Text>
                    ),
                  }
                )
              }
            >
              <Group spacing={8}>
                <CoinsHand />
                Make collection offer
              </Group>
            </Button>
          )}
          <Menu position="bottom-end">
            <Menu.Target>
              <Box>
                <Button px="sm" w={40} color={SECONDARY_COLOR} size="lg">
                  <DotsHorizontal />
                </Button>
              </Box>
            </Menu.Target>

            <Menu.Dropdown>
              <Menu.Item
                component="a"
                href={buildContractTokenUrl(blockchain.type, address)}
                target="_blank"
                rel="noreferrer"
                icon={<EtherscanLogo width={16} />}
              >
                View contract on {getBlockExplorerName(blockchain.type)}
              </Menu.Item>
              {arweaveLink && (
                <Menu.Item
                  component="a"
                  href={arweaveLink}
                  target="_blank"
                  rel="noreferrer"
                  icon={<ArweaveLogo width={16} />}
                >
                  View metadata on Arweave
                </Menu.Item>
              )}
            </Menu.Dropdown>
          </Menu>
        </Group>
      </Group>
      {mintVectorsLoading || isLoading ? (
        <MarketplaceHeaderSkeleton />
      ) : marketplaceCollection ? (
        <MarketplaceFieldsLayout>
          <CollectionStats
            value={totalSoldCount || 0}
            label={`Item${totalSoldCount === 1 ? "" : "s"}`}
          />
          {marketplaceCollection?.ownerCount && (
            <CollectionStats
              value={marketplaceCollection.ownerCount}
              label="Owners"
            />
          )}
          {isMarketplaceEnabledForCollectionChain && !isImported && (
            <CollectionStats
              value={totalMintEarned || 0}
              suffix={currency}
              label="Primary Sales"
            />
          )}
          {isMarketplaceEnabledForCollectionChain && (
            <CollectionStats
              value={
                Math.round(
                  (marketplaceCollection.volume?.allTime || 0) * 10000
                ) / 10000
              }
              suffix={currency}
              label="Secondary Sales"
            />
          )}
          {isMarketplaceEnabledForCollectionChain && (
            <CollectionStats
              value={
                +(marketplaceCollection.tokenCount ?? 0) > 0 &&
                mintVector?.mintVectorStats?.sold
                  ? `${
                      Math.floor(
                        (+(marketplaceCollection.onSaleCount ?? 0) /
                          +(marketplaceCollection.tokenCount ?? 0)) *
                          1000
                      ) / 10
                    }%`
                  : 0
              }
              label="% Listed"
            />
          )}
          {isMarketplaceEnabledForCollectionChain && (
            <CollectionStats
              value={marketplaceCollection.floorAsk?.price?.amount?.native || 0}
              suffix={currency}
              label="Floor Price"
            />
          )}
        </MarketplaceFieldsLayout>
      ) : null}
    </Stack>
  );
};

const CollectionStats = ({
  value,
  suffix,
  label,
}: {
  value: string | number;
  suffix?: string | number;
  label: string;
}) => {
  return (
    <Stack spacing={4}>
      <Box>
        <Text component="span" size={24}>
          {value}
        </Text>{" "}
        {suffix && <Text component="span">{suffix}</Text>}
      </Box>
      <Text color={TEXT_COLOR.PLACEHOLDER} weight={WEIGHT_BOLD} size="xs">
        {label}
      </Text>
    </Stack>
  );
};

const MarketplaceFieldsLayout: FC<PropsWithChildren> = ({ children }) => {
  const theme = useMantineTheme();
  const isMobile = !useMediaQuery(`(min-width: ${theme.breakpoints.sm}px)`);

  return isMobile ? (
    <Grid>
      {Children.toArray(children).map((child, index) => (
        <Grid.Col span={4} key={index}>
          {child}
        </Grid.Col>
      ))}
    </Grid>
  ) : (
    <Group spacing={64}>{children}</Group>
  );
};

export default MarketplaceHeader;
